import { useEffect, useState } from "react";
import api from "../lib/api";
import { TrendingUp, TrendingDown, Info } from "lucide-react";

export default function StockQuotes() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get("/stocks/quotes");
        if (mounted) setData(data);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return <div className="font-mono text-xs uppercase tracking-widest text-black/40 mb-8" data-testid="stocks-loading">Fetching quotes…</div>;
  }
  if (!data) return null;

  const { configured, quotes = [], note } = data;
  const hasRealPrices = quotes.some((q) => (q.price || 0) > 0);

  return (
    <section className="mb-12" data-testid="stock-quotes">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#E63946] mb-1">Live market</div>
          <h2 className="font-serif font-bold text-2xl md:text-3xl tracking-tight">Watchlist</h2>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-black/40">
          {configured ? "Alpha Vantage" : "Configure ALPHAVANTAGE_KEY"}
        </div>
      </div>

      {(!configured || !hasRealPrices) && (
        <div className="flex items-start gap-2 border border-black/10 bg-white p-3 text-xs text-black/60 mb-4" data-testid="stocks-notice">
          <Info className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={1.5} />
          <div>
            {!configured
              ? "Add ALPHAVANTAGE_KEY to backend/.env and restart to show live stock prices. Free key at alphavantage.co."
              : (note || "Prices temporarily unavailable — free tier rate limit hit. Try again in a minute.")}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {quotes.map((q) => {
          const up = (q.change_pct || 0) >= 0;
          const Icon = up ? TrendingUp : TrendingDown;
          return (
            <div key={q.symbol} className="border border-black/10 bg-white p-4" data-testid={`stock-${q.symbol}`}>
              <div className="flex items-center justify-between">
                <div className="font-serif font-bold text-lg tracking-tight">{q.symbol}</div>
                <Icon className={`w-4 h-4 ${up ? "text-emerald-600" : "text-red-500"}`} strokeWidth={1.5} />
              </div>
              <div className="font-mono text-xl font-bold mt-2">
                ${q.price ? q.price.toFixed(2) : "--"}
              </div>
              <div className={`font-mono text-xs mt-1 ${up ? "text-emerald-600" : "text-red-500"}`}>
                {q.price ? `${up ? "+" : ""}${q.change.toFixed(2)} (${up ? "+" : ""}${q.change_pct.toFixed(2)}%)` : "—"}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
